import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AreaOfFraud, Case, CaseStatus } from '../../../api/cases';
import { RootState } from '../../../app/store';
import { fetchCaseInfo, updateCase } from '../../../thunks/Case';
import { updateCaseStage } from '../../caseStages/logic/thunks';
import { createFinalFICMeetingThunk, updateFinalFICMeetingThunk } from '../../reporting/ficDecision/logic/thunks';
import { CaseStageAction } from '../../../api/caseStages';
import { isAccountDetailsEnabled, isInspectionsAndAuditsEnabled, isSanctionsAndDisbursementEnabled } from '../../configs/logic/Slice';
import { Account } from '../../../api/accounts';

export interface CaseHeaderInfoState {
    data: Case | null;
    loadingStatus: 'idle' | 'pending' | 'succeeded' | 'failed';
    error: string | null;
    isUpdating: boolean; // To track PATCH request status
    updateError: string | null;
    isEditMode: boolean;
    editableField: string;
}


const initialState: CaseHeaderInfoState = {
  data: null,
  loadingStatus: 'idle',
  error: null,
  isUpdating: false,
  updateError: null,
  isEditMode: false,
  editableField: '',
};


const caseHeaderSlice = createSlice({
  name: 'caseInfoData',
  initialState,
  reducers: {
    clearCaseHeaderData: (state) => {
      state.data = null;
      state.loadingStatus = 'idle';
      state.error = null;
      state.isUpdating = false;
      state.updateError = null;
    },
    setEditMode: (state, action: PayloadAction<boolean>) => {
      state.isEditMode = action.payload;
    },
    clearUpdateError: (state) => {
      state.updateError = null;
    },
    setEditableField: (state, action: PayloadAction<string>) => {
      state.editableField = action.payload;
    },
    cancelEditMode: (state) => {
      state.updateError = null;
      state.editableField = "";
      state.isEditMode = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // --- Reducers for fetchCaseHeaderData ---
      .addCase(fetchCaseInfo.pending, (state) => {
        state.loadingStatus = 'pending';
        state.error = null;
        state.isUpdating= false;
        state.updateError= null;
        state.isEditMode= false;
        state.editableField= '';
      })
      .addCase(fetchCaseInfo.fulfilled, (state, action) => {
        state.loadingStatus = 'succeeded';
        state.data = action.payload.cases.length > 0? action.payload.cases[0]:null;
        state.error = null;
      })
      .addCase(fetchCaseInfo.rejected, (state, action) => {
        state.loadingStatus = 'failed';
        state.data = null; // Clear data on fetch failure
        state.error = action.payload ?? action.error.message ?? 'Unknown fetch error';
      })
      // --- Reducers for updateCaseHeaderData ---
      .addCase(updateCase.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(updateCase.fulfilled, (state,action) => {
        state.isUpdating = false;
        state.updateError = null;
        state.loadingStatus = 'succeeded'; 

        const request = action.meta.arg.request;

        if (state.data) {
          Object.assign(state.data, request);
        }

      })
      .addCase(updateCase.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = action.payload ?? action.error.message ?? 'Unknown update error';
      })
      .addCase(createFinalFICMeetingThunk.fulfilled, (state, action) => {
        const exposure = action.meta.arg.request.exposure;
        if (state.data && exposure !== undefined) {
          state.data.exposure = exposure;
        }
      })
      .addCase(updateFinalFICMeetingThunk.fulfilled, (state, action) => {
        const exposure = action.meta.arg.request.exposure;
        if (state.data && exposure !== undefined) {
          state.data.exposure = exposure;
        }
      })
      .addCase(updateCaseStage.fulfilled, (state, action) => {
        if (state.data) {
          const case_stage = action.payload?.case_stage;
          state.data.current_stage = case_stage;

          if(case_stage.source_action===CaseStageAction.REJECT_CREATED_CASE || case_stage.source_action===CaseStageAction.REJECT_FWD_CASE)
              state.data.status = CaseStatus.REJECTED
          }
      })
  },
});

export const { clearCaseHeaderData, setEditMode, clearUpdateError, setEditableField, cancelEditMode, } = caseHeaderSlice.actions;

// Export the reducer
export const caseInfoDataReducer = caseHeaderSlice.reducer;

// Selectors to access data from this slice
export const selectCaseHeader = (state: RootState) => state.caseInfoData.data;
export const selectCaseHeaderLoadingStatus = (state: RootState) => state.caseInfoData.loadingStatus;
export const selectCaseHeaderError = (state: RootState) => state.caseInfoData.error;
export const selectCaseHeaderIsUpdating = (state: RootState) => state.caseInfoData.isUpdating;
export const selectCaseHeaderUpdateError = (state: RootState) => state.caseInfoData.updateError;
export const getCurrentCaseProduct = (state: RootState) => state.caseInfoData.data?.product;
export const selectLoanProductInfo = (state: RootState) => state.caseInfoData.data?.loan_product_info ?? [];
export const getCurrentCaseLRIds = createSelector(
  selectLoanProductInfo,
  (loanProductInfo = []) => loanProductInfo.map((loan) => loan.loan_reference_id)
)


export const selectIsEditMode = (state: RootState) => state.caseInfoData.isEditMode;
export const selectEditableField = (state: RootState) => state.caseInfoData.editableField;

export const selectCurrentCaseStage = (state: RootState) =>
  state.caseInfoData.data?.current_stage;

export const selectCaseCreatedBy = (state: RootState) => state.caseInfoData.data?.created_by;
export const selectForwardingAuthority = (state: RootState) =>
  state.caseInfoData.data?.forwarding_authority;

export const selectDealingAuthority = (state: RootState) =>
  state.caseInfoData.data?.dealing_authority;

export const selectIOAppointingAuthority = (state: RootState) =>
  state.caseInfoData.data?.io_app_authority;

export const selectInvestigator = (state: RootState) =>
  state.caseInfoData.data?.investigator_email;

export const getEmployeeIds = (state: RootState) =>state.caseInfoData.data?.employee_ids ?? [];

export const isCreditAreaOfFraud = (state: RootState): boolean =>
  state.caseInfoData.data?.area_of_fraud === AreaOfFraud.CREDIT;

export const isOperationalAreaOfFraud = (state: RootState): boolean =>
  state.caseInfoData.data?.area_of_fraud === AreaOfFraud.OPERATIONAL;

export const isCyberAreaOfFraud = (state: RootState): boolean =>
  state.caseInfoData.data?.area_of_fraud === AreaOfFraud.CYBER;

export const selectAreaOfFraud = (state: RootState): AreaOfFraud | undefined =>
  state.caseInfoData.data?.area_of_fraud;

export const selectAccounts = (state: RootState): Account[] =>
  state.caseInfoData.data?.accounts ?? [];

export const isAccountDetailsNavEnabled = createSelector(
  [isAccountDetailsEnabled, isSanctionsAndDisbursementEnabled, isCreditAreaOfFraud],
  (accountDetailsEnabled, sanctionsAndDisbursementEnabled, creditAreaOfFraud) => {
    return creditAreaOfFraud && (accountDetailsEnabled || sanctionsAndDisbursementEnabled);
  }
);

export const isInspectionsAndAuditsNavEnabled = createSelector(
  [isInspectionsAndAuditsEnabled, isCreditAreaOfFraud],
  (inspectionsAndAuditsEnabled, creditAreaOfFraud): boolean => {
    return inspectionsAndAuditsEnabled && creditAreaOfFraud;
  }
);
